import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { POSTER_TYPES, SELECTORS, AI_MODELS, ORIENTATIONS, UNITS, STANDARD_SIZES, TIME_OF_DAY, TYPOGRAPHY_STYLES, IMAGE_ROLES, IMAGE_POSITIONS, IMAGE_DISPLAY_SIZES, IMAGE_TREATMENTS, IMAGE_PRESERVATION } from "@/lib/catalog";
import TelegramGroupCard from "@/components/TelegramGroupCard";
import EbookDownloadCard from "@/components/EbookDownloadCard";
import ExpiryBanner from "@/components/ExpiryBanner";
import {
  Sparkles, Upload, X, Image as ImageIcon, Palette, Loader2,
  ChevronDown, ChevronUp, Lock as LockIcon, Type, Calendar, MapPin, Clock,
  Sun, Moon, BarChart3, Info, TextCursor, Copy as CopyIcon,
} from "lucide-react";

const SECTION = "rounded-xl border border-white/10 bg-[#0F0F0F]";

function Section({ title, hint, children, defaultOpen = true, testId }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={SECTION} data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-6 py-4 text-left"
      >
        <div>
          <div className="font-display text-lg font-semibold">{title}</div>
          {hint && <div className="text-xs text-neutral-500 mt-0.5">{hint}</div>}
        </div>
        {open ? <ChevronUp size={18} className="text-neutral-500" /> : <ChevronDown size={18} className="text-neutral-500" />}
      </button>
      {open && <div className="px-6 pb-6 pt-1">{children}</div>}
    </div>
  );
}

function OptionGrid({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          onClick={() => onChange(opt)}
          className={`bento-option ${value === opt ? "active" : ""}`}
          data-testid={`${testIdPrefix}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
        >
          {opt}
        </button>
      ))}
    </div>
  );
}

async function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result));
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

export default function Generator() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const prefillFromId = params.get("from") || "";
  const fileRef = useRef(null);

  const [model, setModel] = useState("auto");
  const [posterType, setPosterType] = useState(POSTER_TYPES[0]);
  const [selectors, setSelectors] = useState(
    Object.fromEntries(SELECTORS.map((s) => [s.key, "Auto"]))
  );
  const [customColor, setCustomColor] = useState("#FCA311");
  const [useCustomColor, setUseCustomColor] = useState(false);

  // Size: pick from standard sizes; "custom" reveals manual inputs; "auto" lets AI choose
  const [sizePreset, setSizePreset] = useState("auto");
  const [size, setSize] = useState({ width: 1080, height: 1920, unit: "px" });
  const [orientation, setOrientation] = useState("portrait");

  const [storyline, setStoryline] = useState("");
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [images, setImages] = useState([]); // {dataUrl, base64, mime_type, description, name}
  const [preserveImages, setPreserveImages] = useState(true);

  // Poster text content (optional)
  const [posterText, setPosterText] = useState({ headline: "", tagline: "", ucapan: "" });
  // Typography settings (optional)
  const [typography, setTypography] = useState({
    title_style: "auto",
    body_style: "auto",
    title_color: "#FFFFFF",
    body_color: "#FCA311",
    title_color_enabled: false,
    body_color_enabled: false,
  });
  // Event details (optional, useful for event posters)
  const [eventDetails, setEventDetails] = useState({ date: "", time: "", venue: "" });
  // Infographic data (optional)
  const [infographicData, setInfographicData] = useState("");  // legacy — kept for prefill from old prompts, no longer editable in UI
  // Time of day
  const [timeOfDay, setTimeOfDay] = useState("auto");  // kept for prefill/back-compat; no UI — AI follows storyline
  // Props multi-select + custom text (replaces the single-select `selectors.props` field at submit time)
  const [propsSelected, setPropsSelected] = useState([]);
  const [propsCustom, setPropsCustom] = useState("");

  const [loading, setLoading] = useState(false);
  const [elapsed, setElapsed] = useState(0);        // seconds since submit started
  const [error, setError] = useState("");
  const [errorHint, setErrorHint] = useState("");   // extra actionable hint
  // Prefill state — notifies the user we hydrated from an existing prompt
  const [prefilledFrom, setPrefilledFrom] = useState(null); // {id, poster_type}

  // Tick an elapsed-seconds counter while loading so the user knows progress
  useEffect(() => {
    if (!loading) { setElapsed(0); return; }
    const started = Date.now();
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - started) / 1000)), 250);
    return () => clearInterval(t);
  }, [loading]);

  // Hydrate form from an existing prompt when ?from=<pid> is present
  useEffect(() => {
    if (!prefillFromId) return;
    let cancelled = false;
    (async () => {
      try {
        const { data: doc } = await api.get(`/prompts/${prefillFromId}`);
        if (cancelled || !doc) return;
        if (doc.model) setModel(doc.model);
        if (doc.poster_type) setPosterType(doc.poster_type);
        if (doc.selectors && typeof doc.selectors === "object") {
          setSelectors((prev) => ({ ...prev, ...doc.selectors }));
        }
        if (doc.custom_color) {
          setCustomColor(doc.custom_color);
          setUseCustomColor(true);
        }
        if (doc.size?.width && doc.size?.height) {
          setSize({
            width: doc.size.width,
            height: doc.size.height,
            unit: doc.size.unit || "px",
          });
          setSizePreset("custom");
        }
        if (doc.orientation) setOrientation(doc.orientation);
        if (typeof doc.notes === "string") setStoryline(doc.notes);
        if (typeof doc.additional_info === "string") setAdditionalInfo(doc.additional_info);
        if (typeof doc.preserve_images === "boolean") setPreserveImages(doc.preserve_images);
        if (doc.poster_text) {
          setPosterText({
            headline: doc.poster_text.headline || "",
            tagline: doc.poster_text.tagline || "",
            ucapan: doc.poster_text.ucapan || "",
          });
        }
        if (doc.typography) {
          setTypography((t) => ({
            ...t,
            title_style: doc.typography.title_style || t.title_style,
            body_style: doc.typography.body_style || t.body_style,
            title_color: doc.typography.title_color || t.title_color,
            body_color: doc.typography.body_color || t.body_color,
            title_color_enabled: !!doc.typography.title_color,
            body_color_enabled: !!doc.typography.body_color,
          }));
        }
        if (doc.event_details) {
          setEventDetails({
            date: doc.event_details.date || "",
            time: doc.event_details.time || "",
            venue: doc.event_details.venue || "",
          });
        }
        if (typeof doc.infographic_data === "string") setInfographicData(doc.infographic_data);
        if (doc.time_of_day) setTimeOfDay(doc.time_of_day);
        setPrefilledFrom({ id: doc.id, poster_type: doc.poster_type });
      } catch (err) {
        setError(formatApiError(err.response?.data?.detail) || "Gagal muat prompt asal.");
      }
    })();
    return () => { cancelled = true; };
  }, [prefillFromId]);

  // When a standard preset is picked, sync size + orientation
  useEffect(() => {
    if (sizePreset === "custom") return;
    const preset = STANDARD_SIZES.find((s) => s.id === sizePreset);
    if (preset) {
      setSize({ width: preset.w, height: preset.h, unit: preset.unit });
      if (preset.w > preset.h) setOrientation("landscape");
      else if (preset.w < preset.h) setOrientation("portrait");
      else setOrientation("square");
    }
  }, [sizePreset]);

  const isCustomSize = sizePreset === "custom";
  const isAutoSize = sizePreset === "auto";

  const handleFiles = async (files) => {
    const arr = Array.from(files || []);
    const valid = arr.filter((f) => /^image\/(jpeg|png|webp)$/.test(f.type));
    const objs = await Promise.all(
      valid.map(async (f) => {
        const dataUrl = await fileToBase64(f);
        return {
          dataUrl,
          base64: dataUrl.split(",")[1],
          mime_type: f.type,
          name: f.name,
          // Per-image instruction panel (new)
          label: f.name.replace(/\.[^.]+$/, ""),
          description: "",
          role: "main_subject",
          position: "auto",
          display_size: "medium",
          treatments: ["keep-original"],
          preservation: "identity",
          extra_instructions: "",
        };
      })
    );
    setImages((prev) => [...prev, ...objs]);
  };

  // Merge one image's config into every other image (label & file kept per-image).
  const copySettingsToAll = (srcIndex) => {
    setImages((arr) => {
      const src = arr[srcIndex];
      if (!src) return arr;
      const KEYS = ["role", "position", "display_size", "treatments", "preservation", "extra_instructions"];
      return arr.map((img, idx) => {
        if (idx === srcIndex) return img;
        const patch = {};
        for (const k of KEYS) patch[k] = Array.isArray(src[k]) ? [...src[k]] : src[k];
        return { ...img, ...patch };
      });
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setErrorHint("");
    setLoading(true);
    try {
      // Auto-resolve: if model is "auto", pick a sensible default (Gemini 3 Flash — fast & creative)
      const resolvedModel = model === "auto" ? "gemini-3-flash-preview" : model;
      const isAuto = sizePreset === "auto";

      // Merge multi-selected props + custom-typed props into the selectors.props string.
      // Fallback to "Auto" if nothing chosen so AI can decide freely.
      const propsMerged = [
        ...propsSelected,
        ...propsCustom.split(",").map((s) => s.trim()).filter(Boolean),
      ];
      const selectorsOut = {
        ...selectors,
        props: propsMerged.length > 0 ? propsMerged.join(", ") : "Auto",
      };

      const payload = {
        model: resolvedModel,
        poster_type: posterType,
        selectors: selectorsOut,
        custom_color: useCustomColor ? customColor : null,
        size,
        orientation,
        size_auto: isAuto,
        notes: storyline,
        additional_info: additionalInfo,
        preserve_images: preserveImages,
        poster_text: posterText,
        event_details: eventDetails,
        infographic_data: infographicData,
        time_of_day: "auto",       // UI removed — AI infers time from storyline
        typography: {
          title_style: typography.title_style,
          body_style: typography.body_style,
          title_color: typography.title_color_enabled ? typography.title_color : "",
          body_color: typography.body_color_enabled ? typography.body_color : "",
        },
        images: images.map((i) => ({
          base64: i.base64,
          mime_type: i.mime_type,
          description: i.description,
          label: i.label,
          role: i.role,
          position: i.position,
          display_size: i.display_size,
          treatments: i.treatments || [],
          preservation: i.preservation,
          extra_instructions: i.extra_instructions,
        })),
      };
      // Give the client a bit longer than the server-side hard limit (85s) so the
      // server's own 504 message wins instead of a raw client abort.
      const { data } = await api.post("/generate-prompt", payload, { timeout: 120000 });
      navigate(`/prompts/${data.id}`);
    } catch (err) {
      const status = err.response?.status;
      const detail = formatApiError(err.response?.data?.detail) || err.message || "Ralat tidak diketahui.";
      setError(detail);
      // Actionable hints for common failure modes
      if (status === 504 || /timeout|timed out/i.test(String(err.message))) {
        setErrorHint(
          model === "auto" || model === "gemini-3-flash-preview"
            ? "Cuba semula — atau kurangkan bilangan gambar / panjang storyline."
            : "Cuba tukar ke model 'Gemini 3 Flash' — ia jauh lebih pantas."
        );
      } else if (status === 502 || status === 503 || status === 520) {
        setErrorHint("Pelayan sibuk. Sila cuba semula dalam beberapa saat.");
      } else if (!err.response) {
        // Network error / Cloudflare page (no JSON body)
        setErrorHint("Sambungan terputus atau Cloudflare timeout. Sila cuba semula.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-end justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-500/80 font-semibold mb-2">
            Studio
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold">Prompt Generator Poster Kreatif</h1>
          <p className="text-sm text-neutral-400 mt-2 max-w-xl">
            Reka brief anda, biar AI menyusun prompt sinematik yang siap digunakan untuk mana-mana generator imej.
          </p>
        </div>
      </div>

      <form onSubmit={submit} className="space-y-6" data-testid="generator-form">
        {/* Expiring-soon amber banner (auto-hides for lifetime / >7 days) */}
        <ExpiryBanner thresholdDays={7} />

        {/* Prefilled-from banner */}
        {prefilledFrom && (
          <div
            className="rounded-xl border border-amber-500/40 bg-gradient-to-r from-amber-500/15 to-amber-500/5 p-4 flex items-center gap-4 flex-wrap"
            data-testid="prefilled-banner"
          >
            <div className="w-10 h-10 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center">
              <Sparkles size={16} className="text-amber-400" />
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="font-display text-base font-bold text-amber-300">
                Sunting semula prompt
              </div>
              <div className="text-xs text-neutral-400 mt-0.5 font-mono">
                Semua tetapan prompt asal <span className="text-amber-400">{prefilledFrom.poster_type}</span> telah diisi. Ubah mana-mana bahagian, kemudian klik <span className="text-white">Jana Prompt</span>.
              </div>
            </div>
            <button
              type="button"
              onClick={() => {
                navigate("/generator", { replace: true });
                window.location.reload();
              }}
              className="text-xs text-neutral-400 hover:text-red-400 border border-white/10 hover:border-red-500/40 rounded-md px-3 py-1.5 transition-colors"
              data-testid="clear-prefill-btn"
            >
              Reset borang
            </button>
          </div>
        )}

        {/* Telegram Group banner */}
        <TelegramGroupCard variant="compact" />

        {/* Ebook download — members-only */}
        <EbookDownloadCard variant="compact" />

        {/* AI Model */}
        <Section title="Model AI" hint="Pilih enjin kreatif." testId="section-model">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {AI_MODELS.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setModel(m.id)}
                className={`p-4 rounded-lg border text-left transition-all ${
                  model === m.id
                    ? "border-amber-500 bg-amber-500/10"
                    : "border-white/10 bg-[#121212] hover:border-white/20"
                }`}
                data-testid={`model-${m.id}`}
              >
                <div className="text-xs text-amber-500/70 uppercase tracking-wider font-mono">{m.provider}</div>
                <div className="font-display text-lg font-semibold mt-1">{m.label}</div>
                <div className="text-xs text-neutral-500 mt-1">{m.note}</div>
              </button>
            ))}
          </div>
        </Section>

        {/* Poster Type */}
        <Section title="Jenis Poster" hint={`${POSTER_TYPES.length} kategori`} testId="section-poster-type">
          <OptionGrid
            options={POSTER_TYPES}
            value={posterType}
            onChange={setPosterType}
            testIdPrefix="poster-type"
          />
        </Section>

        {/* Image upload — directly under Jenis Poster */}
        <Section
          title="Gambar Rujukan"
          hint="JPEG/PNG/WEBP. Muat naik satu atau lebih gambar dan beri penerangan setiap satu."
          testId="section-images"
        >
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleFiles(e.dataTransfer.files);
            }}
            className="border-2 border-dashed border-white/10 hover:border-amber-500/40 rounded-lg p-8 text-center cursor-pointer transition-colors"
            onClick={() => fileRef.current?.click()}
            data-testid="image-dropzone"
          >
            <Upload className="mx-auto text-amber-500/70" size={28} />
            <p className="mt-3 text-sm text-neutral-400">
              Drag & drop gambar di sini atau klik untuk pilih.
            </p>
            <p className="text-xs text-neutral-600 mt-1 font-mono">JPEG · PNG · WEBP · boleh muat naik banyak</p>
            <input
              ref={fileRef}
              type="file"
              multiple
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="image-file-input"
            />
          </div>

          {images.length > 0 && (
            <>
              <div className="space-y-4 mt-4">
                {images.map((img, i) => {
                  const setImg = (patch) => setImages((arr) => arr.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
                  const toggleTreat = (id) =>
                    setImages((arr) => arr.map((x, idx) => {
                      if (idx !== i) return x;
                      const cur = x.treatments || [];
                      return { ...x, treatments: cur.includes(id) ? cur.filter((t) => t !== id) : [...cur, id] };
                    }));
                  return (
                    <div
                      key={i}
                      className="rounded-lg bg-[#121212] border border-white/10 overflow-hidden"
                      data-testid={`image-card-${i}`}
                    >
                      {/* Header row: thumbnail + label input + remove */}
                      <div className="flex gap-3 p-3 border-b border-white/10">
                        <img src={img.dataUrl} alt={img.name} className="w-24 h-24 object-cover rounded-md shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="text-[10px] uppercase tracking-wider text-amber-500/80 font-mono">
                              Gambar Rujukan [{i + 1}]
                            </div>
                            <div className="flex items-center gap-1">
                              {images.length > 1 && (
                                <button
                                  type="button"
                                  onClick={() => copySettingsToAll(i)}
                                  className="text-[10px] font-mono text-amber-400 hover:text-amber-300 border border-amber-500/30 hover:border-amber-500/60 rounded px-2 py-1 flex items-center gap-1"
                                  data-testid={`image-copy-all-${i}`}
                                  title="Salin tetapan gambar ini ke semua gambar lain"
                                >
                                  <CopyIcon size={10} /> Salin ke semua
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => setImages((arr) => arr.filter((_, idx) => idx !== i))}
                                className="bg-black/60 hover:bg-red-500 transition-colors rounded p-1.5"
                                data-testid={`image-remove-${i}`}
                              >
                                <X size={14} />
                              </button>
                            </div>
                          </div>
                          <input
                            type="text"
                            value={img.label}
                            onChange={(e) => setImg({ label: e.target.value })}
                            placeholder="Nama / Label (cth: Pasembor)"
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm mb-2 focus:border-amber-500/40 focus:outline-none"
                            data-testid={`image-label-${i}`}
                          />
                          <textarea
                            value={img.description}
                            onChange={(e) => setImg({ description: e.target.value })}
                            placeholder="Penerangan gambar — apa yang ada dalam gambar ini"
                            rows={2}
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm resize-none focus:border-amber-500/40 focus:outline-none"
                            data-testid={`image-description-${i}`}
                          />
                        </div>
                      </div>

                      {/* Role / Position / Size row */}
                      <div className="p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-amber-500/70 font-mono mb-1">Peranan</div>
                          <select
                            value={img.role}
                            onChange={(e) => setImg({ role: e.target.value })}
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm font-mono focus:border-amber-500/40 focus:outline-none"
                            data-testid={`image-role-${i}`}
                          >
                            {IMAGE_ROLES.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
                          </select>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-amber-500/70 font-mono mb-1">Kedudukan</div>
                          <select
                            value={img.position}
                            onChange={(e) => setImg({ position: e.target.value })}
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm font-mono focus:border-amber-500/40 focus:outline-none"
                            data-testid={`image-position-${i}`}
                          >
                            {IMAGE_POSITIONS.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
                          </select>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-amber-500/70 font-mono mb-1">Saiz Paparan</div>
                          <select
                            value={img.display_size}
                            onChange={(e) => setImg({ display_size: e.target.value })}
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm font-mono focus:border-amber-500/40 focus:outline-none"
                            data-testid={`image-size-${i}`}
                          >
                            {IMAGE_DISPLAY_SIZES.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
                          </select>
                        </div>
                      </div>

                      {/* Treatments — multi-select */}
                      <div className="p-3 border-t border-white/5">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-[10px] uppercase tracking-wider text-amber-500/70 font-mono">
                            Kesan / Rawatan Gambar ({(img.treatments || []).length} dipilih)
                          </div>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-1.5">
                          {IMAGE_TREATMENTS.map((t) => {
                            const active = (img.treatments || []).includes(t.id);
                            return (
                              <button
                                key={t.id}
                                type="button"
                                onClick={() => toggleTreat(t.id)}
                                className={`text-left text-xs rounded-md border px-2 py-1.5 transition-all ${
                                  active
                                    ? "border-amber-500 bg-amber-500/10 text-amber-300"
                                    : "border-white/10 bg-[#0A0A0A] text-neutral-400 hover:border-white/20"
                                }`}
                                data-testid={`image-treat-${i}-${t.id}`}
                                aria-pressed={active}
                              >
                                <span className="inline-flex items-center gap-1.5">
                                  <span className={`w-3 h-3 rounded-sm border flex items-center justify-center text-[8px] shrink-0 ${
                                    active ? "border-amber-500 bg-amber-500 text-black" : "border-white/25"
                                  }`}>{active ? "✓" : ""}</span>
                                  <span>{t.label}</span>
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      {/* Preservation + extra instructions */}
                      <div className="p-3 border-t border-white/5 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-amber-500/70 font-mono mb-1">Tahap Kekalkan</div>
                          <select
                            value={img.preservation}
                            onChange={(e) => setImg({ preservation: e.target.value })}
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm font-mono focus:border-amber-500/40 focus:outline-none"
                            data-testid={`image-preservation-${i}`}
                          >
                            {IMAGE_PRESERVATION.map((o) => (<option key={o.id} value={o.id}>{o.label}</option>))}
                          </select>
                        </div>
                        <div>
                          <div className="text-[10px] uppercase tracking-wider text-amber-500/70 font-mono mb-1">Arahan Tambahan</div>
                          <textarea
                            value={img.extra_instructions}
                            onChange={(e) => setImg({ extra_instructions: e.target.value })}
                            placeholder="Cth: Letakkan gambar di tengah. Jangan ubah muka atau pakaian. Tambah outline putih tebal."
                            rows={2}
                            className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm resize-none focus:border-amber-500/40 focus:outline-none"
                            data-testid={`image-extra-${i}`}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <label className="mt-4 flex items-start gap-3 p-3 rounded-md border border-amber-500/30 bg-amber-500/5 cursor-pointer" data-testid="preserve-images-toggle-row">
                <input
                  type="checkbox"
                  checked={preserveImages}
                  onChange={(e) => setPreserveImages(e.target.checked)}
                  className="mt-0.5 accent-amber-500"
                  data-testid="preserve-images-toggle"
                />
                <div className="text-sm">
                  <div className="text-amber-300 font-semibold flex items-center gap-1.5">
                    <LockIcon size={14} /> Global: Kekalkan semua gambar 100% sama
                  </div>
                  <div className="text-xs text-neutral-400 mt-0.5">
                    Override untuk semua gambar — AI tidak akan mengubah wajah, objek atau warna. Setiap gambar boleh set &quot;Tahap Kekalkan&quot; sendiri di atas.
                  </div>
                </div>
              </label>
            </>
          )}
        </Section>

        {/* Storyline / Visual Kreatif — directly under image upload */}
        <Section
          title="Storyline / Visual Kreatif"
          hint="Terangkan cerita atau visual yang anda mahu — AI akan mengikut arahan anda."
          testId="section-storyline"
        >
          <textarea
            value={storyline}
            onChange={(e) => setStoryline(e.target.value)}
            placeholder="Cth: 'Konsert raya Aidilfitri di Stadium Bukit Jalil, pelakon di hadapan dengan baju Melayu hitam-emas, lampu pentas neon, bunga api di belakang.'"
            rows={5}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm focus:border-amber-500/40 focus:outline-none"
            data-testid="storyline-input"
          />
        </Section>

        {/* Maklumat Tambahan — extra brand/product/campaign context */}
        <Section
          title="Maklumat Tambahan"
          hint="Konteks ekstra: nama jenama, produk, tagline kempen, mesej utama, sasaran audiens, gaya cerita."
          testId="section-additional-info"
          defaultOpen={false}
        >
          <div className="flex items-center gap-2 mb-2 text-xs text-neutral-500">
            <Info size={14} className="text-amber-500" />
            <span>Sebarang butiran ekstra yang tidak muat di ruangan Storyline — AI akan gabungkan.</span>
          </div>
          <textarea
            value={additionalInfo}
            onChange={(e) => setAdditionalInfo(e.target.value)}
            placeholder={"Cth:\n• Jenama: Kopi Amanah\n• Produk: Kopi kapsul premium\n• Sasaran: profesional muda 25-40\n• Tone: mesra, bertenaga, mewah\n• Elak: warna hijau, gaya kartun"}
            rows={5}
            className="w-full px-4 py-3 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm focus:border-amber-500/40 focus:outline-none"
            data-testid="additional-info-input"
          />
        </Section>

        {/* Kandungan Teks Poster + Tipografi (MERGED) */}
        <Section
          title="Kandungan Teks pada Poster"
          hint="Tulis teks yang akan muncul + pilih gaya font & warna untuk setiap teks."
          testId="section-poster-text"
          defaultOpen={false}
        >
          {/* HEADLINE / TAJUK — text input + typography controls */}
          <div className="rounded-lg border border-white/10 bg-[#121212] p-4 mb-4">
            <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-500/80 font-mono">
                <Type size={12} /> Headline / Tajuk
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-xs text-neutral-400">
                <input
                  type="checkbox"
                  checked={typography.title_color_enabled}
                  onChange={(e) => setTypography((t) => ({ ...t, title_color_enabled: e.target.checked }))}
                  className="accent-amber-500"
                  data-testid="typo-title-color-toggle"
                />
                Guna warna kustom
              </label>
            </div>
            <input
              type="text"
              value={posterText.headline}
              onChange={(e) => setPosterText((p) => ({ ...p, headline: e.target.value }))}
              placeholder="Cth: SELAMAT HARI RAYA"
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm mb-3 focus:border-amber-500/40 focus:outline-none"
              data-testid="poster-headline"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono mb-1">Font Style</div>
                <select
                  value={typography.title_style}
                  onChange={(e) => setTypography((t) => ({ ...t, title_style: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm focus:border-amber-500/40 focus:outline-none font-mono"
                  data-testid="typo-title-style"
                >
                  {TYPOGRAPHY_STYLES.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
                <div className="text-[10px] text-neutral-500 mt-1 font-mono">
                  {TYPOGRAPHY_STYLES.find((s) => s.id === typography.title_style)?.hint || ""}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono mb-1">Warna</div>
                <div className="flex items-center gap-2">
                  <Palette size={16} className="text-amber-500 shrink-0" />
                  <input
                    type="color"
                    value={typography.title_color}
                    onChange={(e) => setTypography((t) => ({ ...t, title_color: e.target.value }))}
                    disabled={!typography.title_color_enabled}
                    className="h-10 w-14 rounded-md bg-transparent cursor-pointer disabled:opacity-40 shrink-0"
                    data-testid="typo-title-color"
                  />
                  <input
                    type="text"
                    value={typography.title_color}
                    onChange={(e) => setTypography((t) => ({ ...t, title_color: e.target.value }))}
                    disabled={!typography.title_color_enabled}
                    className="flex-1 min-w-0 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white font-mono text-sm disabled:opacity-40"
                    data-testid="typo-title-color-hex"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* TAGLINE + UCAPAN — text inputs + shared body typography controls */}
          <div className="rounded-lg border border-white/10 bg-[#121212] p-4">
            <div className="flex items-center justify-between mb-3 gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-500/80 font-mono">
                <Type size={12} /> Tagline / Sub-teks &amp; Ucapan
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-xs text-neutral-400">
                <input
                  type="checkbox"
                  checked={typography.body_color_enabled}
                  onChange={(e) => setTypography((t) => ({ ...t, body_color_enabled: e.target.checked }))}
                  className="accent-amber-500"
                  data-testid="typo-body-color-toggle"
                />
                Guna warna kustom
              </label>
            </div>
            <input
              type="text"
              value={posterText.tagline}
              onChange={(e) => setPosterText((p) => ({ ...p, tagline: e.target.value }))}
              placeholder="Cth: Maaf Zahir & Batin"
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm mb-3 focus:border-amber-500/40 focus:outline-none"
              data-testid="poster-tagline"
            />
            <textarea
              value={posterText.ucapan}
              onChange={(e) => setPosterText((p) => ({ ...p, ucapan: e.target.value }))}
              placeholder="Ucapan / quote (banyak baris)&#10;Cth: 'Ramadhan datang sekali setahun. Marilah kita penuhi dengan amalan terbaik.'"
              rows={3}
              className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm mb-3 focus:border-amber-500/40 focus:outline-none"
              data-testid="poster-ucapan"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono mb-1">Font Style</div>
                <select
                  value={typography.body_style}
                  onChange={(e) => setTypography((t) => ({ ...t, body_style: e.target.value }))}
                  className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm focus:border-amber-500/40 focus:outline-none font-mono"
                  data-testid="typo-body-style"
                >
                  {TYPOGRAPHY_STYLES.map((s) => (
                    <option key={s.id} value={s.id}>{s.label}</option>
                  ))}
                </select>
                <div className="text-[10px] text-neutral-500 mt-1 font-mono">
                  {TYPOGRAPHY_STYLES.find((s) => s.id === typography.body_style)?.hint || ""}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-mono mb-1">Warna</div>
                <div className="flex items-center gap-2">
                  <Palette size={16} className="text-amber-500 shrink-0" />
                  <input
                    type="color"
                    value={typography.body_color}
                    onChange={(e) => setTypography((t) => ({ ...t, body_color: e.target.value }))}
                    disabled={!typography.body_color_enabled}
                    className="h-10 w-14 rounded-md bg-transparent cursor-pointer disabled:opacity-40 shrink-0"
                    data-testid="typo-body-color"
                  />
                  <input
                    type="text"
                    value={typography.body_color}
                    onChange={(e) => setTypography((t) => ({ ...t, body_color: e.target.value }))}
                    disabled={!typography.body_color_enabled}
                    className="flex-1 min-w-0 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white font-mono text-sm disabled:opacity-40"
                    data-testid="typo-body-color-hex"
                  />
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Maklumat Event */}
        <Section
          title="Maklumat Event"
          hint="Untuk poster event — tarikh, masa, tempat akan dipaparkan pada poster."
          testId="section-event"
          defaultOpen={false}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="flex items-center gap-2 text-xs text-neutral-500 mb-1 font-mono uppercase">
                <Calendar size={12} /> Tarikh
              </label>
              <input
                type="date"
                value={eventDetails.date}
                onChange={(e) => setEventDetails((p) => ({ ...p, date: e.target.value }))}
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-md text-white font-mono text-sm focus:border-amber-500/40 focus:outline-none"
                data-testid="event-date"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs text-neutral-500 mb-1 font-mono uppercase">
                <Clock size={12} /> Masa
              </label>
              <input
                type="text"
                value={eventDetails.time}
                onChange={(e) => setEventDetails((p) => ({ ...p, time: e.target.value }))}
                placeholder="Cth: 8.00 malam"
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm focus:border-amber-500/40 focus:outline-none"
                data-testid="event-time"
              />
            </div>
            <div>
              <label className="flex items-center gap-2 text-xs text-neutral-500 mb-1 font-mono uppercase">
                <MapPin size={12} /> Tempat
              </label>
              <input
                type="text"
                value={eventDetails.venue}
                onChange={(e) => setEventDetails((p) => ({ ...p, venue: e.target.value }))}
                placeholder="Cth: Stadium Bukit Jalil, KL"
                className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm focus:border-amber-500/40 focus:outline-none"
                data-testid="event-venue"
              />
            </div>
          </div>
        </Section>

        {/* Creative selectors rendered as individual sections.
            EXCLUDED:
              • shot_type / camera_angle → rendered together below
              • props                    → rendered separately (multi-select + custom)
              • palette                  → merged with "Warna Kustom" section below */}
        {SELECTORS.filter((s) => !["shot_type", "camera_angle", "props", "palette"].includes(s.key)).map((sel) => (
          <Section key={sel.key} title={sel.label} hint={`${sel.options.length} pilihan · ${sel.key}`} testId={`section-${sel.key}`} defaultOpen={false}>
            <OptionGrid
              options={sel.options}
              value={selectors[sel.key]}
              onChange={(v) => setSelectors((s) => ({ ...s, [sel.key]: v }))}
              testIdPrefix={`selector-${sel.key}`}
            />
          </Section>
        ))}

        {/* Props — MULTI-SELECT + custom input (renders in place of the single-select selector) */}
        {(() => {
          const propsSel = SELECTORS.find((s) => s.key === "props");
          if (!propsSel) return null;
          const opts = propsSel.options.filter((o) => o.toLowerCase() !== "auto");
          const toggleProp = (opt) => {
            setPropsSelected((prev) => (prev.includes(opt) ? prev.filter((x) => x !== opt) : [...prev, opt]));
          };
          const clearAll = () => setPropsSelected([]);
          return (
            <Section
              key="props"
              title={propsSel.label}
              hint={`Pilih seberapa banyak · atau tulis sendiri di ruang kustom`}
              testId="section-props"
              defaultOpen={false}
            >
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="text-xs text-neutral-500 font-mono">
                  {propsSelected.length} dipilih {propsSelected.length > 0 && <>· <span className="text-amber-400">{propsSelected.join(", ")}</span></>}
                </div>
                {propsSelected.length > 0 && (
                  <button
                    type="button"
                    onClick={clearAll}
                    className="text-xs text-neutral-500 hover:text-red-400 border border-white/10 hover:border-red-500/40 rounded-md px-2 py-1"
                    data-testid="props-clear-btn"
                  >
                    Kosongkan semua
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-4">
                {opts.map((opt) => {
                  const active = propsSelected.includes(opt);
                  return (
                    <button
                      key={opt}
                      type="button"
                      onClick={() => toggleProp(opt)}
                      className={`p-2.5 rounded-md border text-left text-sm transition-all ${
                        active
                          ? "border-amber-500 bg-amber-500/10 text-amber-300"
                          : "border-white/10 bg-[#121212] text-neutral-300 hover:border-white/20"
                      }`}
                      data-testid={`props-option-${opt.toLowerCase().replace(/\s+/g, "-")}`}
                      aria-pressed={active}
                    >
                      <span className="flex items-center gap-2">
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                          active ? "border-amber-500 bg-amber-500 text-black" : "border-white/20"
                        }`}>
                          {active ? "✓" : ""}
                        </span>
                        <span>{opt}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-amber-500/80 font-mono mb-1 block">
                  Props Kustom (Tulis Sendiri)
                </label>
                <input
                  type="text"
                  value={propsCustom}
                  onChange={(e) => setPropsCustom(e.target.value)}
                  placeholder="Cth: kunci tembaga antik, cawan kopi retak, kompas emas — pisahkan dgn koma"
                  className="w-full px-3 py-2.5 bg-[#0A0A0A] border border-white/10 rounded-md text-white text-sm focus:border-amber-500/40 focus:outline-none"
                  data-testid="props-custom-input"
                />
                <div className="text-[10px] text-neutral-500 font-mono mt-1">
                  Props tersuai anda akan digabungkan dengan yang dipilih di atas.
                </div>
              </div>
            </Section>
          );
        })()}

        {/* Palet Warna — MERGED with Warna Kustom (single section for all colour choices) */}
        {(() => {
          const paletteSel = SELECTORS.find((s) => s.key === "palette");
          if (!paletteSel) return null;
          return (
            <Section
              key="palette"
              title={paletteSel.label}
              hint="Pilih palet siap atau aktifkan warna kustom di bawah."
              testId="section-palette"
              defaultOpen={false}
            >
              <OptionGrid
                options={paletteSel.options}
                value={selectors[paletteSel.key]}
                onChange={(v) => setSelectors((s) => ({ ...s, [paletteSel.key]: v }))}
                testIdPrefix={`selector-${paletteSel.key}`}
              />
              <div className="mt-5 pt-5 border-t border-white/10">
                <div className="text-[11px] uppercase tracking-wider text-amber-500/80 font-mono mb-3">
                  Warna Kustom (Opsyenal)
                </div>
                <div className="flex items-center gap-4 flex-wrap">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={useCustomColor}
                      onChange={(e) => setUseCustomColor(e.target.checked)}
                      className="accent-amber-500"
                      data-testid="custom-color-toggle"
                    />
                    <span className="text-sm text-neutral-300">Guna warna kustom</span>
                  </label>
                  <div className="flex items-center gap-3">
                    <Palette size={18} className="text-amber-500" />
                    <input
                      type="color"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      disabled={!useCustomColor}
                      className="h-10 w-16 rounded-md bg-transparent cursor-pointer disabled:opacity-40"
                      data-testid="custom-color-input"
                    />
                    <input
                      type="text"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      disabled={!useCustomColor}
                      className="w-32 px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white font-mono text-sm disabled:opacity-40"
                      data-testid="custom-color-hex"
                    />
                  </div>
                </div>
              </div>
            </Section>
          );
        })()}

        {/* Shot type + Camera angle COMBINED in one section */}
        <Section
          title="Shot & Angle Kamera"
          hint="Tetapkan jenis shot dan sudut kamera untuk komposisi visual."
          testId="section-shot-angle"
          defaultOpen={false}
        >
          {SELECTORS.filter((s) => ["shot_type", "camera_angle"].includes(s.key)).map((sel) => (
            <div key={sel.key} className="mb-5 last:mb-0">
              <div className="text-xs uppercase tracking-wider text-amber-500/80 font-mono mb-2">
                {sel.label}
              </div>
              <OptionGrid
                options={sel.options}
                value={selectors[sel.key]}
                onChange={(v) => setSelectors((s) => ({ ...s, [sel.key]: v }))}
                testIdPrefix={`selector-${sel.key}`}
              />
            </div>
          ))}
        </Section>

        {/* Size — standard sizes bento; only when Custom selected, show manual inputs */}
        <Section title="Saiz Poster" hint="Pilih saiz standard atau Custom untuk tetapan sendiri." testId="section-size" defaultOpen={false}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {STANDARD_SIZES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setSizePreset(s.id)}
                className={`bento-option ${sizePreset === s.id ? "active" : ""}`}
                data-testid={`size-preset-${s.id}`}
              >
                <div className="font-medium">{s.label}</div>
                {s.id !== "custom" && (
                  <div className="text-[10px] text-neutral-500 font-mono mt-0.5">
                    {s.w}×{s.h} {s.unit}
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Show current selection summary */}
          {!isCustomSize && !isAutoSize && (
            <div className="mt-4 text-xs text-neutral-500 font-mono">
              Saiz dipilih: <span className="text-amber-400">{size.width}×{size.height} {size.unit}</span> · Orientasi: <span className="text-amber-400">{orientation}</span>
            </div>
          )}
          {isAutoSize && (
            <div className="mt-4 text-xs text-amber-500/80 font-mono" data-testid="size-auto-hint">
              ✨ AI akan cadangkan saiz optimum ikut jenis poster & storyline anda.
            </div>
          )}

          {/* Custom inputs appear only when Custom is selected */}
          {isCustomSize && (
            <div className="mt-5 p-4 rounded-lg border border-amber-500/20 bg-amber-500/[0.03]" data-testid="custom-size-panel">
              <div className="text-xs uppercase tracking-wider text-amber-500/80 font-mono mb-3">
                Tetapan Saiz Sendiri
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs text-neutral-500 mb-1 font-mono uppercase">Lebar</label>
                  <input
                    type="number"
                    min="1"
                    step="any"
                    value={size.width}
                    onChange={(e) => setSize((s) => ({ ...s, width: Number(e.target.value) }))}
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white font-mono text-sm"
                    data-testid="size-width"
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1 font-mono uppercase">Tinggi</label>
                  <input
                    type="number"
                    min="1"
                    step="any"
                    value={size.height}
                    onChange={(e) => setSize((s) => ({ ...s, height: Number(e.target.value) }))}
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white font-mono text-sm"
                    data-testid="size-height"
                  />
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1 font-mono uppercase">Unit</label>
                  <select
                    value={size.unit}
                    onChange={(e) => setSize((s) => ({ ...s, unit: e.target.value }))}
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white font-mono text-sm"
                    data-testid="size-unit"
                  >
                    {UNITS.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-neutral-500 mb-1 font-mono uppercase">Orientasi</label>
                  <select
                    value={orientation}
                    onChange={(e) => setOrientation(e.target.value)}
                    className="w-full px-3 py-2 bg-[#0A0A0A] border border-white/10 rounded-md text-white font-mono text-sm"
                    data-testid="size-orientation"
                  >
                    {ORIENTATIONS.map((o) => (
                      <option key={o.id} value={o.id}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </Section>

        {/* Submit */}
        <div className="sticky bottom-4 z-10">
          <div className="bg-[#101010] border border-white/10 rounded-xl p-4 backdrop-blur-xl shadow-2xl shadow-black/60">
            {error && (
              <div className="text-sm bg-red-500/10 border border-red-500/20 rounded-md px-4 py-3 mb-3" data-testid="generator-error">
                <div className="text-red-400">{error}</div>
                {errorHint && (
                  <div className="text-xs text-amber-300 mt-1.5 flex items-start gap-1.5" data-testid="generator-error-hint">
                    <span className="text-amber-500">→</span>
                    <span>{errorHint}</span>
                  </div>
                )}
              </div>
            )}
            {loading && (
              <div className="flex items-center justify-between gap-3 mb-3 px-4 py-2.5 bg-amber-500/5 border border-amber-500/20 rounded-md" data-testid="generator-progress">
                <div className="flex items-center gap-2 text-xs text-amber-300 font-mono">
                  <Loader2 size={12} className="animate-spin" />
                  <span>Menjana prompt… <span className="text-white" data-testid="elapsed-seconds">{elapsed}s</span></span>
                </div>
                {elapsed >= 40 && elapsed < 70 && (
                  <span className="text-[10px] text-neutral-500 font-mono">Sabar, model AI sedang berfikir…</span>
                )}
                {elapsed >= 70 && (
                  <span className="text-[10px] text-amber-400 font-mono">Hampir siap — jangan tutup halaman.</span>
                )}
              </div>
            )}
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="text-xs text-neutral-500 font-mono">
                Model: <span className="text-amber-400">{AI_MODELS.find((m) => m.id === model)?.label || model}</span> · {images.length} gambar · {isAutoSize ? "Saiz: AUTO" : `${size.width}×${size.height} ${size.unit}`}
              </div>
              <button
                type="submit"
                disabled={loading}
                className="btn-primary flex items-center gap-2"
                data-testid="generate-submit-btn"
              >
                {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                {loading ? `Menjana… ${elapsed}s` : (error ? "Cuba Semula" : "Jana Prompt")}
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
